'use client';

import {
  Alert,
  Box,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { KeyValuePairEditor } from '../../../components/KeyValuePairEditor';
import type { SubmitJobWizardState } from '../submitJobWizardState';

export const SubmitJobStep = (props: StepProps<SubmitJobWizardState>) => {
  const dal = useDAL();
  const { wizardState: state, setWizardState } = props;
  const isGcp = state.workspaceCloud.toLowerCase() === 'gcp';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (partial: Partial<SubmitJobWizardState>) =>
    setWizardState({ ...state, ...partial });

  const isValidEntryPointUrl = isGcp
    ? /^gs:\/\/.+/.test(state.entryPoint.trim())
    : /^s3[a]?:\/\/.+/.test(state.entryPoint.trim());

  const canSubmit =
    state.name.trim().length >= 3 && isValidEntryPointUrl && !submitting;

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const args = state.entryPointArguments
        .trim()
        .split(/\s+/)
        .filter((arg) => arg.length > 0);

      const sparkConfig = Object.fromEntries(
        state.sparkConfig
          .filter((p) => p.key.trim() && p.value.trim())
          .map((p) => [p.key.trim(), p.value.trim()])
      );

      const envVars = Object.fromEntries(
        state.environmentVariables
          .filter((p) => p.key.trim() && p.value.trim())
          .map((p) => [p.key.trim(), p.value.trim()])
      );

      await dal.lakehouse.startSparkJob(state.workspaceId, {
        sparkEngineId: state.sparkEngineId,
        name: state.name.trim(),
        directParams: {
          ...(state.jobTimeout && {
            jobTimeout: parseInt(state.jobTimeout, 10),
          }),
          ...(state.retryEnabled && {
            retryPolicy: {
              maxAttempts: state.maxRetries,
              cooldownInterval: state.cooldownInterval,
            },
          }),
          sparkSubmitParams: {
            entryPoint: state.entryPoint.trim(),
            ...(args.length > 0 && { entryPointArguments: args }),
            ...(Object.keys(sparkConfig).length > 0 && {
              sparkSubmitParameters: sparkConfig,
            }),
            ...(Object.keys(envVars).length > 0 && {
              environmentVariables: envVars,
            }),
          },
        },
      });
      void props.finish('Job submitted successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit job');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StepContainer
      hideStepper
      stepperIndex={0}
      canGoNext={() => canSubmit}
      nextButtonText={submitting ? 'Submitting...' : 'Submit'}
      nextButtonLoadingText='Submitting...'
      backButtonText='Cancel'
      onBackClick={props.back}
      onNextClick={handleSubmit}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Details */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Details</Typography>
              <Typography variant='body2' color='text.secondary'>
                Name this job submission on engine{' '}
                <strong>{state.sparkEngineName}</strong>.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant='subtitle1'>Job Name</Typography>
              </FormLabel>
              <TextField
                size='small'
                placeholder='Enter job name'
                value={state.name}
                onChange={(e) => update({ name: e.target.value })}
                error={state.name.length > 0 && state.name.trim().length < 3}
                fullWidth
              />
              {state.name.length > 0 && state.name.trim().length < 3 && (
                <FormHelperText error>
                  Name must be at least 3 characters
                </FormHelperText>
              )}
            </FormControl>
          </Stack>
        </Box>

        {/* Spark Application */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Spark Application</Typography>
              <Typography variant='body2' color='text.secondary'>
                Specify the application entry point and any arguments to pass to
                it.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <Stack gap='8px'>
                <FormLabel>
                  <Typography variant='subtitle1'>Entry Point</Typography>
                </FormLabel>
                <Typography variant='body2' color='text.secondary'>
                  {isGcp
                    ? 'GCS path to your Spark application JAR or Python script'
                    : 'S3 path to your Spark application JAR or Python script'}
                </Typography>
              </Stack>
              <TextField
                size='small'
                placeholder={
                  isGcp
                    ? 'gs://bucket/path/to/application.jar'
                    : 's3://bucket/path/to/application.jar'
                }
                value={state.entryPoint}
                onChange={(e) => update({ entryPoint: e.target.value })}
                error={state.entryPoint.length > 0 && !isValidEntryPointUrl}
                fullWidth
                sx={{ mt: '16px' }}
              />
              {state.entryPoint.length > 0 && !isValidEntryPointUrl && (
                <FormHelperText error>
                  {isGcp
                    ? 'Entry point must be a valid GCS URL (gs://)'
                    : 'Entry point must be a valid S3 URL (s3:// or s3a://)'}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth>
              <Stack gap='8px'>
                <FormLabel>
                  <Typography variant='subtitle1'>
                    Entry Point Arguments
                  </Typography>
                </FormLabel>
                <Typography variant='body2' color='text.secondary'>
                  Space-separated arguments to pass to the entry point
                </Typography>
              </Stack>
              <TextField
                size='small'
                placeholder='arg1 arg2 arg3'
                value={state.entryPointArguments}
                onChange={(e) =>
                  update({ entryPointArguments: e.target.value })
                }
                fullWidth
                sx={{ mt: '16px' }}
              />
            </FormControl>
          </Stack>
        </Box>

        {/* Spark Configuration */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Spark Configuration</Typography>
              <Typography variant='body2' color='text.secondary'>
                Define Spark configuration properties as key-value pairs.
              </Typography>
            </Stack>

            <KeyValuePairEditor
              pairs={state.sparkConfig}
              onChange={(sparkConfig) => update({ sparkConfig })}
              keyPlaceholder='spark.executor.memory'
              valuePlaceholder='4g'
            />
          </Stack>
        </Box>

        {/* Environment Variables */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Environment Variables</Typography>
              <Typography variant='body2' color='text.secondary'>
                Define environment variables to set when running the Spark
                application.
              </Typography>
            </Stack>

            <KeyValuePairEditor
              pairs={state.environmentVariables}
              onChange={(environmentVariables) =>
                update({ environmentVariables })
              }
              keyPlaceholder='MY_VAR'
              valuePlaceholder='my_value'
            />
          </Stack>
        </Box>

        {/* Retry Policy */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
            >
              <Stack gap='8px'>
                <Typography variant='h6'>Retry Policy</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Automatically retry the job on failure with a cooldown
                  interval between attempts.
                </Typography>
              </Stack>
              <Switch
                checked={state.retryEnabled}
                onChange={(e) => update({ retryEnabled: e.target.checked })}
              />
            </Stack>

            <Stack direction='row' gap='16px'>
              <FormControl fullWidth disabled={!state.retryEnabled}>
                <FormLabel>
                  <Typography variant='subtitle1' color='inherit'>
                    Max Retries
                  </Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  disabled={!state.retryEnabled}
                  value={state.retryEnabled ? state.maxRetries : ''}
                  onChange={(e) =>
                    update({
                      maxRetries: Math.max(1, parseInt(e.target.value) || 1),
                    })
                  }
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>

              <FormControl fullWidth disabled={!state.retryEnabled}>
                <FormLabel>
                  <Typography variant='subtitle1' color='inherit'>
                    Cooldown Interval (seconds)
                  </Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  disabled={!state.retryEnabled}
                  value={state.retryEnabled ? state.cooldownInterval : ''}
                  onChange={(e) =>
                    update({
                      cooldownInterval: Math.max(
                        1,
                        parseInt(e.target.value) || 1
                      ),
                    })
                  }
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>
            </Stack>
          </Stack>
        </Box>

        {/* Job Timeout */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Job Timeout</Typography>
              <Typography variant='body2' color='text.secondary'>
                Set the maximum time the job is allowed to run before it is
                terminated.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant='subtitle1'>Timeout (seconds)</Typography>
              </FormLabel>
              <TextField
                size='small'
                type='number'
                placeholder='No timeout'
                value={state.jobTimeout}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^\d+$/.test(val)) {
                    update({ jobTimeout: val });
                  }
                }}
                slotProps={{ htmlInput: { min: 1 } }}
                fullWidth
              />
            </FormControl>
          </Stack>
        </Box>
      </Stack>

      {error && (
        <Box paddingX='min(100px, 6vw)' paddingTop='24px'>
          <Alert severity='error' sx={{ maxWidth: '540px' }}>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        </Box>
      )}
    </StepContainer>
  );
};
